<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-between space-x-2">
            <div class="space-y-1">
                <ULink to="/customers" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
                    <span>Customers</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="family" class="flex items-center space-x-2">
                    <h1 class="text-2xl font-semibold text-gray-700">{{ family.name || 'Family' }}</h1>
                    <UButton
                        v-if="auth.hasRole(UserRole.admin)"
                        @click="overlays.editName = true"
                        color="gray"
                        variant="ghost"
                        icon="i-ph-pencil-simple-bold"
                        size="sm"
                    />
                </div>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="members"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-users-three-duotone', label: 'No family members.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', tbody: 'divide-y divide-gray-200', tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
                class="h-full"
                @select="(row: any) => navigateTo(`/customers/${row.customer.id}`)"
            >
                <template #id-data="{ row }">
                    <ULink :to="`/customers/${row.customer.id}`" class="font-mono hover:underline">
                        {{ row.customer.number.toString().padStart(8, '0') }}
                    </ULink>
                </template>
                <template #customer-data="{ row }">
                    <div class="leading-5">
                        <p>{{ [row.customer.first_name, row.customer.middle_name, row.customer.last_name].filter(Boolean).join(' ') }}</p>
                        <span class="text-sm capitalize">{{ [row.customer.gender, age(row.customer.birth_date), row.customer.blood_type].filter(Boolean).join(' · ') }}</span>
                    </div>
                </template>
                <template #contact-data="{ row }">
                    <div class="leading-5">
                        <PhoneLabel v-if="row.customer.phone" :value="row.customer.phone" />
                        <p v-if="row.customer.email" :class="row.customer.phone && 'text-sm'">{{ row.customer.email }}</p>
                        <UBadge v-if="!row.customer.email && !row.customer.phone" color="orange" variant="subtle">
                            Unregistered
                        </UBadge>
                    </div>
                </template>
                <template #partner-data="{ row }">
                    <div v-if="row.customer.subscriptions?.find((s: Subscription) => s.id == row.customer.subscription_id)?.partner" class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="row.customer.subscriptions.find((s: Subscription) => s.id == row.customer.subscription_id).partner.name" :path="row.customer.subscriptions.find((s: Subscription) => s.id == row.customer.subscription_id).partner.avatar" />
                        <span>{{ row.customer.subscriptions.find((s: Subscription) => s.id == row.customer.subscription_id).partner.name }}</span>
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #policy_holder-data="{ row }">
                    <div class="leading-5">
                        <p v-if="row.customer?.policy_holder">{{ row.customer.policy_holder }}</p>
                        <span v-else class="text-gray-300">–</span>
                    </div>
                </template>
                <template #subscription-data="{ row }">
                    <BadgeSubscriptionStatus with-expiry :subscription="row.customer?.subscriptions?.find((s: Subscription) => s.id == row.customer.subscription_id)" />
                </template>
                <template #role-data="{ row }">
                    <FamilyMemberRoleBadge :role="row.role" />
                </template>
            </UTable>
        </div>

        <OverlayFamilyEditNameModal v-model:open="overlays.editName" @reload="fetchData" :family="family" />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import type { Family, FamilyMember } from '~/types/family'
import type { Subscription } from '~/types/subscription'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'View Family' })

const route = useRoute()
const client = useSupabaseClient()
const auth = useAuthStore()

const { age } = useNumber()

const loading = ref(false)
const family = ref<Family>()
const members = ref<FamilyMember[]>([])

const overlays = reactive({
    editName: false,
})

const columns = [
    { key: 'id', label: 'ID' },
    { key: 'customer', label: 'Customer' },
    { key: 'contact', label: 'Registered Contact' },
    { key: 'partner', label: 'Partner' },
    { key: 'policy_holder', label: 'Policy Holder' },
    { key: 'subscription', label: 'Subscription' },
    { key: 'role', label: 'Role' },
]

onMounted(() => fetchData())

async function fetchData() {
    loading.value = true
    members.value = []

    const { data, error } = await client
        .from('families')
        .select(`
            id,
            name,
            created_by,
            created_at,
            updated_at,
            members:family_members!family_members_family_id_fkey (
                id,
                role,
                status,
                customer:customers!family_members_customer_id_fkey (
                    id,
                    number,
                    user_id,
                    subscription_id,
                    first_name,
                    middle_name,
                    last_name,
                    email,
                    phone,
                    avatar,
                    birth_date,
                    gender,
                    blood_type,
                    policy_holder,
                    updated_at,
                    subscriptions (id, source_id, source_type, is_active, expires_at, partner:partners (id, name, avatar))
                )
            )
        `)
        .eq('id', route.params.id)
        .single()

    if (data) {
        family.value = data as Family
        members.value = (data.members || []) as FamilyMember[]
    } else if (error) {
        console.error(error)
    }

    loading.value = false
}
</script>
